import * as React from "react";
import { IconPhoto, IconPlayerPlay } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import {
  aspectRatioToClass,
  type MetaAspectRatio,
  type RatioGroupKey,
} from "@/config/contentProgramUpload";

/**
 * The contact sheet: creative grouped by Meta placement, each group a grid
 * whose column width is tuned to that ratio so a row of 9:16 reels and a
 * row of 16:9 videos come out at about the same height, and every row runs
 * edge to edge. Tiles never lift, scale or glow; the chosen one carries the
 * brand ring and everything else is quiet.
 */

/** Where each ratio runs on Meta, in the app's voice. */
export const PLACEMENT_LABEL: Record<RatioGroupKey, string> = {
  "9:16": "Stories and reels",
  "4:5": "Feed",
  "1:1": "Feed and carousel",
  "16:9": "In-stream video",
  unknown: "Other sizes",
};

/** Minimum column width per ratio; rows fill the card and tiles land near one height. */
const SHEET_COLUMNS: Record<RatioGroupKey, string> = {
  "9:16": "grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))]",
  "4:5": "grid-cols-[repeat(auto-fill,minmax(11rem,1fr))]",
  "1:1": "grid-cols-[repeat(auto-fill,minmax(13rem,1fr))]",
  "16:9": "grid-cols-[repeat(auto-fill,minmax(20rem,1fr))]",
  unknown: "grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))]",
};

export function ratioClass(ratio: RatioGroupKey | null | undefined, fallback: MetaAspectRatio = "9:16") {
  return aspectRatioToClass(ratio && ratio !== "unknown" ? ratio : fallback);
}

/** The grid for one ratio. Tiles are width-led; the ratio gives the height. */
export function MediaSheet({
  ratio,
  className,
  children,
}: {
  ratio: RatioGroupKey;
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={cn("grid gap-3", SHEET_COLUMNS[ratio], className)}>{children}</div>;
}

/** A placement group inside a sheet: a quiet title with the ratio beside it. */
export function RatioSection({
  ratio,
  count,
  children,
}: {
  ratio: RatioGroupKey;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-baseline gap-2">
        <h3 className="text-[13px] font-medium text-foreground">{PLACEMENT_LABEL[ratio]}</h3>
        <span className="text-xs tabular-nums text-muted-foreground">
          {ratio === "unknown" ? "" : ratio}
          {count !== undefined && (ratio === "unknown" ? `${count}` : ` · ${count}`)}
        </span>
      </div>
      {children}
    </section>
  );
}

/**
 * The frame every tile shares: full width of its grid cell, ratio-led
 * height, muted backing, hairline ring. Media fills it; children lay over.
 */
export function MediaFrame({
  ratio,
  poster,
  isVideo = false,
  selected = false,
  className,
  children,
}: {
  ratio: RatioGroupKey | null | undefined;
  poster?: string | null;
  isVideo?: boolean;
  selected?: boolean;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        "relative block w-full overflow-hidden rounded-md bg-muted",
        ratioClass(ratio),
        selected ? "ring-2 ring-brand-600 ring-offset-2 ring-offset-card" : "ring-1 ring-border",
        className
      )}
    >
      {poster ? (
        <img src={poster} alt="" className="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
          <IconPhoto size={20} stroke={1.5} aria-hidden />
        </span>
      )}
      {isVideo && poster && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="flex size-9 items-center justify-center rounded-full bg-black/55 text-white">
            <IconPlayerPlay size={16} stroke={2} className="ml-0.5 fill-current" aria-hidden />
          </span>
        </span>
      )}
      {children}
    </span>
  );
}

/** The scrim at the foot of a tile: one line of title, one muted line. */
export function TileFoot({ title, note }: { title: string; note?: string | null }) {
  return (
    <span className="pointer-events-none absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 via-black/50 to-transparent px-2.5 pb-2 pt-8">
      <span className="line-clamp-2 block text-[12px] font-medium leading-tight text-white">{title}</span>
      {note && <span className="mt-0.5 block truncate text-[11px] text-white/70">{note}</span>}
    </span>
  );
}

/** A dot and a word over media, for a tile's state. */
export function TileStatus({ tone, label }: { tone: "amber" | "emerald" | "grey"; label: string }) {
  return (
    <span className="absolute left-2 top-2 z-10 inline-flex items-center gap-1.5 rounded bg-black/55 px-1.5 py-0.5 text-[11px] font-medium text-white">
      <span
        aria-hidden
        className={cn(
          "size-1.5 rounded-full",
          tone === "amber" && "bg-amber-400",
          tone === "emerald" && "bg-emerald-400",
          tone === "grey" && "bg-neutral-400"
        )}
      />
      {label}
    </span>
  );
}

/** Bones for a sheet: one grid of tiles at the given ratio. */
export function MediaSheetSkeleton({ count = 6, ratio = "9:16" }: { count?: number; ratio?: RatioGroupKey }) {
  return (
    <MediaSheet ratio={ratio} className="animate-pulse">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={cn("w-full rounded-md bg-muted", ratioClass(ratio))} />
      ))}
    </MediaSheet>
  );
}
